// PROTOTYPE ONLY (087 Super Match, ticket 10). See bestMatchPrototype.data.ts.
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { variantNames, type BestMatchScenario, type BestMatchVariantKey } from './bestMatchPrototype.data';

const variantKeys = Object.keys(variantNames) as BestMatchVariantKey[];

export type BestMatchPrototypeControls = {
  enabled: boolean;
  variant: BestMatchVariantKey;
  scenario: BestMatchScenario;
  cycle: (step: number) => void;
  setScenario: (scenario: BestMatchScenario) => void;
};

/** Local dev plus UAT, so stakeholders can review the approved treatment. */
const prototypesAllowed = import.meta.env.DEV || import.meta.env.VITE_DATADOG_ENV === 'uat';

export const useBestMatchPrototype = (): BestMatchPrototypeControls => {
  const [searchParams, setSearchParams] = useSearchParams();
  const enabled = prototypesAllowed && searchParams.has('bestMatchPrototype');
  const raw = searchParams.get('bestMatchPrototype');
  const variant: BestMatchVariantKey = raw && raw in variantNames ? (raw as BestMatchVariantKey) : 'A';
  const scenario = (searchParams.get('bestMatchScenario') as BestMatchScenario | null) || 'live';

  const patch = useCallback(
    (entries: Record<string, string>) =>
      setSearchParams(
        (current) => {
          Object.entries(entries).forEach(([key, value]) => current.set(key, value));
          return current;
        },
        { replace: true },
      ),
    [setSearchParams],
  );

  const cycle = useCallback(
    (step: number) =>
      patch({
        bestMatchPrototype:
          variantKeys[(variantKeys.indexOf(variant) + step + variantKeys.length) % variantKeys.length],
      }),
    [patch, variant],
  );

  useEffect(() => {
    if (!enabled) return;
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (target?.matches('input, textarea, [contenteditable]')) return;
      if (event.key === 'ArrowLeft') cycle(-1);
      if (event.key === 'ArrowRight') cycle(1);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [cycle, enabled]);

  return {
    enabled,
    variant,
    scenario,
    cycle,
    setScenario: (next) => patch({ bestMatchScenario: next }),
  };
};
